import { useEffect, useRef, useState } from "react";

const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 30000;
const MAX_INITIAL_FAILURES = 3;

/**
 * @param {string | undefined} sid
 * @param {boolean} enabled
 */
export function useAplanStream(sid, enabled) {
  const [aplan, setAplan] = useState(/** @type {any} */(null));
  const [connected, setConnected] = useState(false);
  const [supported, setSupported] = useState(true);
  const esRef = useRef(/** @type {EventSource | null} */(null));
  const retryRef = useRef(0);
  const everOpenedRef = useRef(false);
  const timerRef = useRef(/** @type {any} */(null));

  useEffect(() => {
    setAplan(null);
    setSupported(true);
    everOpenedRef.current = false;
    retryRef.current = 0;
  }, [sid]);

  useEffect(() => {
    if (!enabled || !sid) return;
    let cancelled = false;

    const connect = () => {
      if (cancelled) return;

      const token = localStorage.getItem("token");
      const url =
        `${process.env.REACT_APP_PLANNER_API_END_POINT}/device/` +
        `${encodeURIComponent(sid)}/aplan/stream?token=${encodeURIComponent(token || "")}`;

      const es = new EventSource(url);
      esRef.current = es;

      es.onopen = () => {
        everOpenedRef.current = true;
        retryRef.current = 0;
        setConnected(true);
      };

      const handle = (/** @type {MessageEvent} */ e) => {
        try {
          setAplan({ ...JSON.parse(e.data), receivedAt: Date.now() });
        } catch (err) {
          console.warn("Bad aplan frame", err);
        }
      };
      es.addEventListener("snapshot", handle);
      es.addEventListener("aplan", handle);

      es.onerror = () => {
        setConnected(false);
        es.close();
        if (cancelled) return;

        // EventSource hides the status code. A stream that never opened is
        // almost certainly 403/404 (tenant not enabled, BLT device, flag off) —
        // retrying forever would hammer the API for every such user.
        if (!everOpenedRef.current && retryRef.current >= MAX_INITIAL_FAILURES) {
          setSupported(false);
          return;
        }

        const delay =
          Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * 2 ** retryRef.current) *
          (0.5 + Math.random()); // jitter: avoid a synchronized wave after a restart
        retryRef.current += 1;
        timerRef.current = setTimeout(connect, delay);
      };
    };

    connect();

    return () => {
      cancelled = true;
      clearTimeout(timerRef.current);
      esRef.current?.close();
      esRef.current = null;
      setConnected(false);
    };
  }, [sid, enabled]);

  return { aplan, connected, supported };
}

export function useLiveRemaining(aplan) {
  const [, tick] = useState(0);

  useEffect(() => {
    if (!aplan?.watering) return;
    const id = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [aplan?.watering]);

  if (!aplan?.watering) return null;
  const elapsed = Math.floor((Date.now() - aplan.receivedAt) / 1000);
  return {
    valveSec: Math.max(0, aplan.valveRemainingMin * 60 - elapsed),
    totalSec: Math.max(0, aplan.totalRemainingMin * 60 - elapsed),
  };
}

export const fmt = (sec) =>
  `${String(Math.floor(sec / 60)).padStart(2, "0")}:${String(sec % 60).padStart(2, "0")}`;